import type { HTMLAttributes, TdHTMLAttributes, ThHTMLAttributes } from "react";
import { cn } from "../../lib/cn";
import { formatCurrency } from "../../lib/format";

// ---------------------------------------------------------------------------
// Table — semantic wrapper around <table>
// ---------------------------------------------------------------------------

export function Table({ className, ...props }: HTMLAttributes<HTMLTableElement>) {
  return (
    <div className="-mx-4 w-full overflow-x-auto sm:mx-0">
      <table className={cn("w-full border-collapse text-sm", className)} {...props} />
    </div>
  );
}

export function TableHead({ className, ...props }: HTMLAttributes<HTMLTableSectionElement>) {
  return (
    <thead
      className={cn("font-medium text-ink-500 text-xs uppercase tracking-wide", className)}
      {...props}
    />
  );
}

export function TableBody({ className, ...props }: HTMLAttributes<HTMLTableSectionElement>) {
  return <tbody className={cn("divide-y divide-ink-100", className)} {...props} />;
}

export function TableRow({ className, ...props }: HTMLAttributes<HTMLTableRowElement>) {
  return <tr className={cn("transition-colors hover:bg-ink-50/60", className)} {...props} />;
}

export function TableHeaderCell({
  className,
  align = "left",
  ...props
}: ThHTMLAttributes<HTMLTableCellElement> & { align?: "left" | "right" | "center" }) {
  return (
    <th
      className={cn(
        "border-ink-200 border-b px-3 py-2.5 font-medium",
        align === "right" && "text-right",
        align === "center" && "text-center",
        align === "left" && "text-left",
        className,
      )}
      {...props}
    />
  );
}

export function TableCell({
  className,
  align = "left",
  ...props
}: TdHTMLAttributes<HTMLTableCellElement> & { align?: "left" | "right" | "center" }) {
  return (
    <td
      className={cn(
        "px-3 py-2.5 text-ink-800",
        align === "right" && "tabular text-right",
        align === "center" && "text-center",
        className,
      )}
      {...props}
    />
  );
}

// ---------------------------------------------------------------------------
// Money — pre-formatted currency cell with optional negative styling
// ---------------------------------------------------------------------------

export interface MoneyProps {
  amount: string | number;
  currency?: string;
  /** Apply intent-based coloring for negative/zero values. */
  emphasize?: boolean;
  /**
   * Hva negative belop betyr i denne konteksten:
   *  - "balance" (default): negativ = noen skylder noe → rod.
   *  - "expense": negativ = refusjon/depositum-retur → gronn (positivt for
   *    den som har lagt ut).
   */
  intent?: "balance" | "expense";
  className?: string;
}

export function Money({
  amount,
  currency = "NOK",
  emphasize,
  intent = "balance",
  className,
}: MoneyProps) {
  const value = typeof amount === "string" ? Number.parseFloat(amount) : amount;
  const isNegative = value < 0;
  const negativeClass =
    intent === "expense"
      ? "text-emerald-700 dark:text-emerald-300"
      : "text-rose-600 dark:text-rose-400";
  return (
    <span
      className={cn(
        "tabular",
        emphasize && isNegative && negativeClass,
        emphasize && value === 0 && "text-ink-400",
        className,
      )}
    >
      {formatCurrency(value, currency)}
    </span>
  );
}
